import * as React from 'react'

import { useTheme } from 'next-themes'
import { DropdownMenuItem } from '~/sotano-kit/primitives/dropdown-menu'
import { SunIcon } from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { Button } from '~/sotano-kit/primitives/button'

export const ThemeSwitcher = () => {
  const { setTheme, theme } = useTheme()
  const [focusedThemeSwitcher, setFocusedThemeSwitcher] = React.useState(
    theme ?? 'dark'
  )

  React.useEffect(() => {
    setFocusedThemeSwitcher(theme ?? 'dark')
  }, [theme])

  return (
    <DropdownMenuItem
      className="hover:bg-transparent focus:bg-transparent"
      onSelect={(e) => e.preventDefault()}
      onMouseOutCapture={(e) => {
        const relatedTarget = e.relatedTarget
        if (!(relatedTarget instanceof HTMLElement)) return
        const newTargetIsWithin = e.currentTarget.contains(relatedTarget)
        if (newTargetIsWithin) return
        setFocusedThemeSwitcher(theme ?? 'dark')
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') setTheme(focusedThemeSwitcher)
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          setFocusedThemeSwitcher(theme ?? 'dark')
        }
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

        if (focusedThemeSwitcher === 'system') {
          setFocusedThemeSwitcher(e.key === 'ArrowRight' ? 'dark' : 'light')
        } else if (focusedThemeSwitcher === 'dark') {
          setFocusedThemeSwitcher(e.key === 'ArrowRight' ? 'light' : 'system')
        } else {
          setFocusedThemeSwitcher(e.key === 'ArrowRight' ? 'system' : 'dark')
        }
      }}
    >
      <span>Theme</span>

      <div className="ml-auto space-x-0.5">
        <Button
          unstyled
          focusStyled
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Space') {
              setTheme('system')
            }
          }}
          onClick={() => setTheme('system')}
          role="theme-switcher-system"
          className={clsx(
            focusedThemeSwitcher === 'system'
              ? 'bg-dark-elevated'
              : 'hover:text-dark-bright text-dark-faint',
            'focus-visible:outline-none focus-visible:bg-dark-elevated focus:bg-dark-elevated p-1 rounded cursor-default'
          )}
        >
          <ScreenIcon />
        </Button>
        <Button
          unstyled
          focusStyled
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Space') {
              setTheme('dark')
            }
          }}
          onClick={() => setTheme('dark')}
          role="theme-switcher-dark"
          className={clsx(
            focusedThemeSwitcher === 'dark'
              ? 'bg-dark-elevated'
              : 'hover:text-dark-bright text-dark-faint',
            'focus-visible:outline-none focus-visible:bg-dark-elevated focus:bg-dark-elevated p-1 rounded cursor-default'
          )}
        >
          <MoonIcon />
        </Button>
        <Button
          unstyled
          focusStyled
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Space') {
              setTheme('light')
            }
          }}
          onClick={() => setTheme('light')}
          role="theme-switcher-light"
          className={clsx(
            focusedThemeSwitcher === 'light'
              ? 'bg-dark-elevated'
              : 'hover:text-dark-bright text-dark-faint',
            'focus-visible:outline-none focus-visible:bg-dark-elevated focus:bg-dark-elevated p-1 rounded cursor-default'
          )}
        >
          <SunIcon />
        </Button>
      </div>
    </DropdownMenuItem>
  )
}

const MoonIcon = () => {
  return (
    <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="m8.036 1.502.298.04.292.053.265.06.325.09.252.082.277.104.239.1.113.053.328.165.321.185.292.191.245.177.32.26.223.2.16.158.12.124.163.18.2.24.152.2c.204.279.387.574.546.883l.074.148.077.166.113.268a6.536 6.536 0 0 1 .385 1.415l.036.277A6.6 6.6 0 0 1 6.91 14.65l-.267-.02-.317-.04a6.571 6.571 0 0 1-1.103-.26l-.244-.086-.302-.122-.261-.12-.263-.135a6.619 6.619 0 0 1-.371-.217l-.176-.115-.187-.13-.121-.09-.188-.15-.26-.224-.072-.067a6.628 6.628 0 0 1-.21-.206c-.15-.153-.031-.405.183-.416A6.602 6.602 0 0 0 7.82 1.902c-.123-.176.003-.424.216-.4Zm1.586 1.464a7.603 7.603 0 0 1-4.749 10.146A5.6 5.6 0 1 0 9.623 2.966Z"
        fill="currentColor"
      />
    </svg>
  )
}

const ScreenIcon = () => {
  return (
    <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.75 3.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25H1.75ZM.5 3.75c0-.69.56-1.25 1.25-1.25h12.5c.69 0 1.25.56 1.25 1.25v7.5c0 .69-.56 1.25-1.25 1.25H1.75c-.69 0-1.25-.56-1.25-1.25v-7.5Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.208 11.558a.45.45 0 0 1 .36.525l-.276 1.467h3.416l-.275-1.467a.45.45 0 0 1 .884-.166l.353 1.882a.55.55 0 0 1-.54.651H5.87a.55.55 0 0 1-.54-.651l.353-1.882a.45.45 0 0 1 .525-.36Z"
        fill="currentColor"
      />
    </svg>
  )
}
